import { Component, createRef } from "react"

/* Modal */
import Modal from "react-modal"

/* Moment */
import moment from "moment"
import "moment/locale/ru"

/* Icons */
import { LoadingOutlined } from "@ant-design/icons"

/* REST API */
import { get, status as changeStatus } from "../../api/Queue"

/* Constants */
import { env } from "../../constants"

/* Components */
import { Avatar, Icon } from "../../components"

/* Helpers */
import { utils } from "../../helpers"

/* Widgets */
import { toast, confirm } from "../common"
import CancelModal from "./CancelModal"
import AssignModal from "./AssignModal"

/* Shared */
import { ACTIONS, EVENTS, duration, line, queueIcon, sourceOf, statusOf } from "./shared"

moment.locale("ru")
Modal.setAppElement("#root")


/* The questions the panel asks before it moves a ticket by hand */
const QUESTIONS = {
    CALLED: { title: "Вызвать клиента?", text: "Клиент получит уведомление о том, что подошла его очередь", confirmText: "Да, вызвать", danger: false },
    IN_SERVICE: { title: "Начать обслуживание?", text: "Талон перейдёт в статус «Обслуживается»", confirmText: "Да, начать", danger: false },
    DONE: { title: "Завершить обслуживание?", text: "Талон будет закрыт", confirmText: "Да, завершить", danger: false },
    MISSED: { title: "Клиент не пришёл?", text: "Талон будет отмечен как несостоявшийся", confirmText: "Да, отметить", danger: true },
    WAITING: { title: "Вернуть в ожидание?", text: "Талон снова встанет в очередь", confirmText: "Да, вернуть", danger: false }
}


/* Widget Details Modal — the card of one ticket, a panel which slides in from the right.
   It is opened by the reference: this._details.current.open(item), so the page keeps
   nothing about it in its own state */
class DetailsModal extends Component {

    constructor() {
        super()

        this.state = {

            visible: false,

            /* The row of the list — it is drawn at once, while the card is still loading */
            item: null,

            status: "loading",
            data: null,

            acting: ""
        }

        /* The form of the cancellation and the form of the master are opened by the reference */
        this._cancel = createRef()
        this._assign = createRef()
    }

    /* Open the card of the ticket */
    open = item => {
        this.setState({ visible: true, item, status: "loading", data: null, acting: "" }, () => this.load())
    }

    /* Close the card */
    close = () => {
        this.setState({ visible: false, item: null, data: null })
    }

    /* Load the card of the ticket */
    load = () => {

        /* Fields */
        const { item } = this.state

        /* Check Data */
        if (!item) {
            return
        }

        get({ id: item.id }).then(response => {
            if (response && response.status === 200) {
                this.setState({ status: "ok", data: response.data })
            }
            else {
                this.setState({ status: "error" })
            }
        }).catch(() => this.setState({ status: "error" }))
    }

    /* Something changed — the card reads itself again and the list behind it too */
    reload = () => {

        const { onChanged } = this.props

        this.load()

        if (onChanged) {
            onChanged()
        }
    }

    /* One action of the card. Отмена asks for the reason in a form of its own,
       the rest only ask whether it is really meant */
    act = action => {

        /* Fields */
        const { data, acting } = this.state

        /* The previous action is still on its way */
        if (acting.length > 0 || !data) {
            return
        }

        /* The cancellation is written down together with its reason */
        if (action === "CANCELLED") {
            this._cancel.current.open(data.item)
            return
        }

        const question = QUESTIONS[action]

        confirm({
            title: question.title,
            text: question.text,
            confirmText: question.confirmText,
            cancelText: "Нет",
            danger: question.danger
        }).then(confirmed => {

            if (!confirmed) {
                return
            }

            this.setState({ acting: action })

            changeStatus({ id: data.item.id, status: action }).then(response => {

                this.setState({ acting: "" })

                if (response && response.status === 200) {
                    toast.success("Статус талона изменён")
                    this.reload()
                }
                else {
                    toast.error("Не удалось изменить статус талона")
                }
            }).catch(() => {
                this.setState({ acting: "" })
                toast.error("Не удалось изменить статус талона")
            })
        })
    }

    /* The head of the card — the point, the number of the ticket and its state */
    _head = () => {

        /* Fields */
        const { item, data } = this.state

        const card = data ? data.item : item
        const image = card.organizationImage ? `${env.mediapoint}organizations/${card.organizationImage}` : null
        const state = statusOf(card.status)

        return (
            <div className="queue-details-head">

                <div className="queue-details-head-top">

                    <Avatar className="queue-details-avatar" src={image} alt={card.organization}>
                        {queueIcon()}
                    </Avatar>

                    <div className="queue-details-title">

                        <div className="queue-details-name">Талон №{utils.check(card.ticketNumber, card.id)}</div>

                        <div className="queue-details-subtitle">
                            <span>{utils.check(card.organization, "Организация удалена")}</span>
                            <span>{sourceOf(card.source)}</span>
                            <span>встал в очередь {moment(card.createdAt).format("DD.MM.YYYY HH:mm")}</span>
                        </div>

                        <div className="queue-details-tags">
                            <div className={`tag ${state.color}`}>{state.text}</div>
                            {card.position ? <div className="tag grey">Позиция {card.position}</div> : null}
                            {card.total > 0 ? <div className="tag orange">{utils.convertor(card.total, "₸", 1)}</div> : null}
                        </div>

                    </div>

                    <div onClick={this.close} className="queue-details-close">
                        <Icon name="close" size={11} width={2.2} />
                    </div>

                </div>

            </div>
        )
    }

    /* The buttons which act on the ticket */
    _actions = () => {

        /* Fields */
        const { data, acting } = this.state

        const current = `${data.item.status}`
        const available = ACTIONS.filter(action => action.from.indexOf(current) > -1)

        return (
            <div className="queue-details-actions">

                <div onClick={() => this._assign.current.open(data.item, data.members)} className="queue-details-action">
                    <Icon name="user" size={16} />
                    <span>{data.item.memberID ? "Сменить мастера" : "Назначить мастера"}</span>
                </div>

                {available.map(action =>
                    <div
                        key={action.key}
                        onClick={() => this.act(action.key)}
                        className={`queue-details-action ${action.primary ? "primary" : ""} ${action.danger ? "danger" : ""} ${acting === action.key ? "disabled" : ""}`}
                    >
                        {acting === action.key ? <LoadingOutlined /> : <Icon name={action.icon} size={16} />}
                        <span>{action.text}</span>
                    </div>
                )}

                {available.length === 0 && <div className="queue-details-closed">Талон закрыт — действий больше нет</div>}

            </div>
        )
    }

    /* One event of the history */
    _event = item => {

        /* What exactly happened */
        const text = utils.check(EVENTS[item.action], "Изменение талона")

        /* Where the ticket moved */
        const from = item.oldStatus ? statusOf(item.oldStatus).text : ""
        const to = item.newStatus ? statusOf(item.newStatus).text : ""
        const moved = from.length > 0 && to.length > 0 && from !== to ? `${from} → ${to}` : ""

        return (
            <div key={`${item.id}`} className="queue-details-log">

                <div className="queue-details-log-line" />

                <div className="queue-details-log-body">
                    <div className="queue-details-log-title">{text}</div>
                    {moved.length > 0 && <div className="queue-details-log-value">{moved}</div>}
                    {item.comment && <div className="queue-details-log-value">{item.comment}</div>}
                    <div className="queue-details-log-text">
                        {moment(item.createdAt).format("DD.MM.YYYY HH:mm")} · {item.member ? item.member : "Администратор"}
                    </div>
                </div>

            </div>
        )
    }

    /* The path of the ticket — how long the client waited and how long he was served */
    _timeline = item => {

        /* The moments the ticket really lived through */
        const steps = [
            { text: "Встал в очередь", value: item.createdAt },
            { text: "Вызван", value: item.calledAt },
            { text: "Обслуживание начато", value: item.serviceStartedAt },
            { text: "Обслуживание завершено", value: item.serviceFinishedAt },
            { text: "Отмечен как неявка", value: item.missedAt }
        ].filter(step => step.value)

        /* How long the client waited for the call and how long the service itself took */
        const waited = item.calledAt ? moment(item.calledAt).diff(moment(item.createdAt), "minutes") : null
        const served = item.serviceStartedAt && item.serviceFinishedAt ? moment(item.serviceFinishedAt).diff(moment(item.serviceStartedAt), "minutes") : null

        return (
            <div className="queue-details-section">

                <div className="queue-details-section-text">Путь талона</div>

                {steps.map(step => line(step.text, moment(step.value).format("DD.MM.YYYY HH:mm")))}

                {waited !== null && line("Ждал вызова", duration(waited), "accent")}
                {served !== null && line("Обслуживание заняло", duration(served), "accent")}

            </div>
        )
    }

    /* The content of the card */
    _content = () => {

        /* Fields */
        const { status, data } = this.state

        if (status === "loading") {
            return (
                <div className="queue-details-loading">
                    <LoadingOutlined />
                </div>
            )
        }

        if (status !== "ok" || !data) {
            return (
                <div className="queue-details-loading">
                    <Icon name="alert" size={38} width={1.5} />
                    <p>Не удалось загрузить талон</p>
                    <div onClick={() => this.setState({ status: "loading" }, () => this.load())} className="queue-details-button">Повторить</div>
                </div>
            )
        }

        const { item, services, events } = data

        return (
            <div className="queue-details-body">

                {this._actions()}

                <div className="queue-details-columns">

                    <div className="queue-details-column">

                        <div className="queue-details-section">

                            <div className="queue-details-section-text">Талон</div>

                            {line("Организация", utils.check(item.organization, "—"))}
                            {line("Адрес", item.organizationAddress)}
                            {line("Телефон организации", item.organizationPhone)}
                            {line("Мастер", item.member ? item.member : "Не назначен")}
                            {line("Позиция в очереди", item.position ? `${item.position}` : null)}
                            {line("Источник", sourceOf(item.source))}
                            {line("Канал", item.channel)}
                            {line("Сумма", item.total > 0 ? utils.convertor(item.total, "₸", 1) : null)}
                            {line("Комментарий", item.comment)}
                            {line("Причина отмены", item.cancelReason, "danger")}

                        </div>

                        <div className="queue-details-section">

                            <div className="queue-details-section-text">Клиент</div>

                            {line("Имя", utils.check(item.clientName, "Без имени"))}
                            {line("Телефон", utils.check(item.phone, "—"), "accent")}
                            {line("Карточка клиента", item.clientID ? utils.check(item.client, `#${item.clientID}`) : "Не заведена")}
                            {line("Email", item.clientEmail)}

                        </div>

                        <div className="queue-details-section">

                            <div className="queue-details-section-text">Услуги</div>

                            {services.length === 0 && <div className="queue-details-empty">Услуги не выбраны</div>}

                            {services.map(service =>
                                <div key={`${service.id}`} className="queue-details-row">

                                    <div className="queue-details-row-main">
                                        <div className="queue-details-row-title">{service.name}</div>
                                        <div className="queue-details-row-text">
                                            {utils.check(service.sessionTime, "—")} мин
                                            {parseInt(service.active) === 0 ? " · в архиве" : ""}
                                        </div>
                                    </div>

                                    <div className="queue-details-row-side">
                                        <div className="queue-details-row-price">
                                            {parseInt(service.isFixedPrice) === 0 && service.endPrice
                                                ? `${utils.convertor(service.price, "", 1)}—${utils.convertor(service.endPrice, "₸", 1)}`
                                                : utils.convertor(service.price, "₸", 1)
                                            }
                                        </div>
                                    </div>

                                </div>
                            )}

                        </div>

                    </div>

                    <div className="queue-details-column">

                        {this._timeline(item)}

                        <div className="queue-details-section">

                            <div className="queue-details-section-text">История талона</div>

                            {events.length === 0 && <div className="queue-details-empty">История пуста</div>}

                            {events.map(event => this._event(event))}

                        </div>

                    </div>

                </div>

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { visible, item } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={this.close}
                className="queue-details-modal"
                overlayClassName="queue-details-overlay"
                closeTimeoutMS={300}
            >
                {item &&
                    <div className="queue-details">

                        {this._head()}
                        {this._content()}

                        {/* Draw Cancel */}
                        <CancelModal ref={this._cancel} onSaved={this.reload} />

                        {/* Draw Assign */}
                        <AssignModal ref={this._assign} onSaved={this.reload} />

                    </div>
                }
            </Modal>
        )
    }
}

export default DetailsModal
